import dayjs from 'dayjs';
import { fetchRealityCheckData } from '@/lib/reality-check';
import type { RealityCheckResponse } from '@/types/reality-check';

type RealityCheckCallback = (data: {
    playedTime: number;
    profitAndLoss: number;
    interval: number;
    currentTimer: number;
    timer: number;
}) => void;

type RealityCheckModalCallback = () => void;

export class RealityCheckService {
    private isInitialized = false;
    private loggedIn = false;
    private accountId: string | null = null;
    private timeOfLastCheck: dayjs.Dayjs | null = null;
    private timeout: ReturnType<typeof setTimeout> | null = null;
    private timer = 0;
    private onDataUpdate: RealityCheckCallback | null = null;
    private onShowModal: RealityCheckModalCallback | null = null;

    public initialize(
        isLoggedIn: boolean,
        accountId: string | null,
        onDataUpdate: RealityCheckCallback,
        onShowModal: RealityCheckModalCallback
    ): void {
        this.loggedIn = isLoggedIn;
        this.accountId = accountId;
        this.onDataUpdate = onDataUpdate;
        this.onShowModal = onShowModal;
        this.isInitialized = true;

        this.makeRealityCheckCall(false);
    }

    public async makeRealityCheckCall(displayModal = true): Promise<void> {
        if (!this.loggedIn || !this.accountId || !this.isInitialized) {
            return;
        }

        try {
            const userData = await fetchRealityCheckData(this.accountId);

            if (!userData) {
                console.warn('[RealityCheckService] No data returned from API');
                return;
            }

            const { playedTime, profitAndLoss, interval, currentTimer, timer } = this.calculateTimerData(userData);

            if (this.onDataUpdate) {
                this.onDataUpdate({
                    playedTime,
                    profitAndLoss,
                    interval,
                    currentTimer,
                    timer
                });
            }

            if (displayModal && this.onShowModal) {
                this.onShowModal();

                if (interval > 0 && !this.timeout) {
                    const fullIntervalMs = interval * 60 * 1000;
                    this.startTimer(fullIntervalMs);
                }

                return;
            }

            if (timer > 0) {
                this.startTimer(timer);
            }
        } catch (error) {
            console.error('[RealityCheckService] Error making reality check call:', error);
        }
    }

    private calculateTimerData(userData: RealityCheckResponse): {
        playedTime: number;
        profitAndLoss: number;
        interval: number;
        currentTimer: number;
        timer: number;
    } {
        const playedTime = userData.session_time_minutes || 0;
        const profitAndLoss = userData.amount_wagered || 0;
        const interval = userData.reality_check_interval || 0;

        const intervalSeconds = interval * 60;
        const sessionTimeSeconds = userData.session_time_seconds || 0;

        let currentTimer = 0;
        let timer = 0;

        if (intervalSeconds > 0) {
            currentTimer = sessionTimeSeconds % intervalSeconds;
            timer = (intervalSeconds - currentTimer) * 1000;
        }

        return {
            playedTime,
            profitAndLoss,
            interval,
            currentTimer,
            timer
        };
    }

    public startTimer(timerMs: number): void {
        if (!this.loggedIn || !this.isInitialized) {
            return;
        }

        if (timerMs <= 0) {
            return;
        }

        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        this.timeOfLastCheck = dayjs();
        this.timer = timerMs;

        this.timeout = setTimeout(() => {
            this.makeRealityCheckCall(true);
        }, timerMs);
    }

    /**
     * Verify if reality check timer has expired (i think it is used when knowldgement message comes from other apps)
     */
    public verifyRealityCheckTimer(): void {
        if (!this.timeOfLastCheck) {
            return;
        }

        const now = dayjs();
        const timePassed = now.diff(this.timeOfLastCheck, 'ms');
        const showModal = timePassed > this.timer;

        this.makeRealityCheckCall(showModal);
    }

    public async continueRealityCheck(): Promise<void> {
        await this.makeRealityCheckCall(false);
    }

    public destroy(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
        this.isInitialized = false;
        this.loggedIn = false;
        this.accountId = null;
        this.onDataUpdate = null;
        this.onShowModal = null;
    }

    public isServiceInitialized(): boolean {
        return this.isInitialized;
    }
}

export const realityCheckService = new RealityCheckService();
